'use client';

/**
 * useBookingCalendar — selezione date + prezzi + slot per i layout del modale prenotazione.
 * Estratto da BookingDatesStep per riusare lo STESSO motore (griglia/hook del DateRangePicker
 * condiviso) in tutti i layout (2-fasi / split / stepper) senza duplicare la logica.
 */

import { useCallback, useEffect, useState } from 'react';
import { getSlotAvailability, type SlotAvailability } from '@/services/bookingService';
import { useDateRangeSelection } from '@/components/ui/date-range-picker/useDateRangeSelection';
import { useDatePrices } from '@/components/ui/date-range-picker/useDatePrices';
import { toLocalDateStr } from '@/components/ui/date-range-picker/calendarUtils';
import type { BlockedDateRange } from '@/components/ui/date-range-picker/types';

interface Params {
  isOpen: boolean;
  boatId: string;
  blockedDates?: BlockedDateRange[];
  initialStartDate?: string;
  initialEndDate?: string;
  /** Layer prezzo per-porto (F-B): rifetcha i prezzi-cella quando cambia. */
  departurePointId?: string;
}

export function useBookingCalendar({ isOpen, boatId, blockedDates = [], initialStartDate = '', initialEndDate = '', departurePointId }: Params) {
  const [gridMonth, setGridMonth] = useState(() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() };
  });
  const [slotAvailability, setSlotAvailability] = useState<Record<string, SlotAvailability>>({});

  const sel = useDateRangeSelection({ initialStartDate, initialEndDate, blockedDates, isOpen });
  const { priceMap, basePrice, loading: priceLoading } = useDatePrices(boatId, gridMonth.year, gridMonth.month, departurePointId);

  useEffect(() => {
    if (!isOpen || !boatId) { setSlotAvailability({}); return; }
    (async () => {
      try {
        const today = new Date();
        const later = new Date(today); later.setMonth(later.getMonth() + 3);
        const r = await getSlotAvailability(boatId, toLocalDateStr(today), toLocalDateStr(later), '13:00');
        setSlotAvailability(r.availability || {});
      } catch (e) { console.error('slot availability', e); }
    })();
  }, [isOpen, boatId]);

  const onMonthChange = useCallback((y: number, m: number) => setGridMonth({ year: y, month: m }), []);

  // Props pronte per <DateRangePickerGrid />.
  const gridProps = {
    startDate: sel.startDate,
    endDate: sel.endDate,
    selectingEnd: sel.selectingEnd,
    onDayClick: sel.handleDayClick,
    isDateBlocked: sel.isDateBlocked,
    priceMap,
    basePrice,
    priceLoading,
    slotAvailability,
    showPrices: !!boatId,
    onMonthChange,
  };

  // Giorni effettivi: data singola (solo start, no end) = 1 giorno (sel.days sarebbe 0).
  const days = sel.startDate && !sel.endDate ? 1 : sel.days;

  return {
    startDate: sel.startDate,
    endDate: sel.endDate,
    days,
    handleClear: sel.handleClear,
    setRange: sel.setRange,
    isRange: !!(sel.startDate && sel.endDate && sel.days > 1),
    gridProps,
  };
}
